import A_CircleButton from '@/components/A_Atoms/A_CircleButton/A_CircleButton';
import styles from './M_ContentCard.module.css';
import A_ContentLink from '@/components/A_Atoms/A_ContentLink/A_ContentLink';
import A_Tag from '@/components/A_Atoms/A_Tag/A_Tag';
import W_AudioBar from '@/components/W_Wrappers/W_AudioBar/W_AudioBar';
import cn from 'classnames';
import { useState, useEffect } from 'react';
import { Q_Icon } from '@/components/Q_Quarks/Q_Icon/Q_Icon';
import { getMediaUrl } from '@/utils/mediaUrl';

export interface ContentItem {
  id: string;
  type: 'image' | 'video' | 'gif' | 'link' | 'audio';
  url: string;
  previewUrl?: string;
  caption?: string;
  title?: string;
  description?: string;
}

interface M_ContentCardProps {
  content: ContentItem;
  onClick?: () => void;
}

export default function M_ContentCard({
  content,
  onClick,
}: M_ContentCardProps) {
  const mediaUrl = getMediaUrl(content.url);
  const previewMediaUrl = getMediaUrl(content.previewUrl || content.url);
  const [staticImageUrl, setStaticImageUrl] = useState<string>('');
  const [videoDuration, setVideoDuration] = useState<string>('');
  const [isVideoPlaying, setIsVideoPlaying] = useState<boolean>(false);

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.ceil(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePlayVideo = () => {
    setIsVideoPlaying(true);
  };

  const handleCloseVideo = () => {
    setIsVideoPlaying(false);
  };

  useEffect(() => {
    if (content.type === 'video') {
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.preload = 'metadata';

      video.onloadedmetadata = () => {
        const duration = formatDuration(video.duration);
        setVideoDuration(duration);

        video.currentTime = 0.1;
        video.onseeked = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (ctx) {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            ctx.drawImage(video, 0, 0);
            const staticUrl = canvas.toDataURL('image/png');
            setStaticImageUrl(staticUrl);
          }
        };
      };

      video.onerror = e => {};

      video.oncanplay = () => {
        if (
          video.duration &&
          !isNaN(video.duration) &&
          isFinite(video.duration)
        ) {
          const duration = formatDuration(video.duration);
          setVideoDuration(duration);
        }
      };

      video.src = mediaUrl;
    }
  }, [mediaUrl, content.type]);

  const renderContent = () => {
    switch (content.type) {
      case 'image':
        return (
          <div>
            <div className={styles.content_wrapper}>
              <img
                src={previewMediaUrl}
                alt={content.caption || 'Изображение'}
                loading='lazy'
              />
            </div>
            {content.caption && (
              <div className={styles.description_wrapper}>
                <p className={cn(styles.description, 'text-caption')}>
                  {content.caption}
                </p>
              </div>
            )}
          </div>
        );

      case 'video':
        return (
          <div className={styles.wrapper}>
            {isVideoPlaying ? (
              <div className={styles.content_wrapper}>
                <video
                  src={mediaUrl}
                  autoPlay
                  className={styles.video_player}
                />
                <A_Tag
                  label={videoDuration || 'VIDEO'}
                  className={styles.timer_tag}
                />
                <div className={styles.video_button}>
                  <A_CircleButton
                    icon={
                      <Q_Icon.CrossIcon width={24} height={24} fill='none' />
                    }
                    variant='glass'
                    onClick={handleCloseVideo}
                  />
                </div>
              </div>
            ) : (
              <div className={styles.content_wrapper}>
                <img
                  src={staticImageUrl || mediaUrl}
                  alt={content.caption || 'Видео'}
                  loading='lazy'
                />
                <A_Tag
                  label={videoDuration || 'VIDEO'}
                  className={styles.timer_tag}
                />
                <div className={styles.video_button}>
                  <A_CircleButton
                    icon={
                      <Q_Icon.PlayIcon width={24} height={24} fill='none' />
                    }
                    variant='glass'
                    onClick={handlePlayVideo}
                  />
                </div>
              </div>
            )}
            {content.caption && (
              <div className={styles.description_wrapper}>
                <p className={cn(styles.description, 'text-caption')}>
                  {content.caption}
                </p>
              </div>
            )}
          </div>
        );

      case 'gif':
        return (
          <div>
            <div className={styles.content_wrapper}>
              <img
                src={previewMediaUrl}
                alt={content.caption || 'GIF'}
                loading='lazy'
              />
              <A_Tag label={'GIF'} className={styles.format_tag} />
            </div>
            {content.caption && (
              <div className={styles.description_wrapper}>
                <p className={cn(styles.description, 'text-caption')}>
                  {content.caption}
                </p>
              </div>
            )}
          </div>
        );

      case 'link':
        return (
          <div className={styles.description_wrapper}>
            {content.description && (
              <p className={cn(styles.description, 'text-caption')}>
                {content.description}
              </p>
            )}
            <A_ContentLink link={content.url} />
          </div>
        );

      case 'audio':
        return (
          <div className={styles.description_wrapper}>
            {content.description && (
              <p className={cn(styles.description, 'text-caption')}>
                {content.description}
              </p>
            )}
            <W_AudioBar audioUrl={mediaUrl} />
          </div>
        );

      default:
        return null;
    }
  };

  return <div onClick={onClick}>{renderContent()}</div>;
}
